/**
 * Holiday API client — Session 3.8.
 */

import { apiFetch, apiDownload, apiUrl } from './api';
import type { LeaveType } from '@jeanieiq/types';
// Return to Work reuses hrApi's types — one definition of the form, whichever
// section it is submitted from.
import type { ReturnToWorkPayload, PublicReturnToWorkForm } from './hrApi';

// 2026-05-12 — short-form values match the leave_requests_leave_type_check
// DB constraint. Long-form 'annual_leave' / 'sick_leave' never matched
// the constraint and caused 500s on insert.
export type { LeaveType } from '@jeanieiq/types';

export type LeaveStatus = 'pending' | 'approved' | 'declined' | 'cancelled' | 'taken';
export type BradfordThreshold = 'low' | 'medium' | 'high' | 'trigger';

export interface CalendarEvent {
  id: string;
  employeeId: string;
  employeeInitials: string;
  employeeName: string;
  leaveType: LeaveType;
  status: LeaveStatus;
  startDate: string;
  endDate: string;
  /** Null for parental leave, which is counted in weeks, not working days. */
  workingDays: number | null;
}

export interface CalendarBankHoliday {
  date: string;
  name: string;
}

export interface CalendarPayload {
  rangeStart: string;
  rangeEnd: string;
  events: CalendarEvent[];
  bankHolidays: CalendarBankHoliday[];
  countryCode: string;
  countryPackBadge: string;
  stats: {
    onLeaveToday: number;
    onLeaveTodayAnnual: number;
    onLeaveTodaySick: number;
    pendingRequests: number;
    leaveTakenAvgDays: number;
    coverWarnings: number;
  };
  coverWarningDates: string[];
}

export interface PublicLeaveRequest {
  id: string;
  employeeId: string;
  employeeFirstName: string;
  employeeLastName: string;
  leaveType: LeaveType;
  startDate: string;
  endDate: string;
  workingDays: number;
  status: LeaveStatus;
  employeeNote: string | null;
  managerNote: string | null;
  requestedAt: string;
  reviewedBy: string | null;
  /** The reviewer's name. Null if undecided, or if that user no longer exists —
   *  `reviewedBy` still holds the id either way, so the audit record stands. */
  reviewedByName: string | null;
  reviewedAt: string | null;
  /** Neonatal: the birth. Parental bereavement: the death. */
  keyDate: string | null;
  neonatalCareDays: number | null;
  /** Set when the booking went beyond a statutory limit and was kept. */
  limitWarning: string | null;
  /** Pharmacy companies only: another live request of the same person's over some of these days. */
  overlaps?: { startDate: string; endDate: string; status: LeaveStatus } | null;
}

export interface ListLeaveRequestsResult {
  requests: PublicLeaveRequest[];
  stats: { pending: number; approved: number; declined: number };
}

export interface EntitlementRow {
  employeeId: string;
  employeeInitials: string;
  employeeName: string;
  department: string | null;
  contractType: 'full-time' | 'part-time' | 'casual' | 'contractor';
  totalEntitlementDays: number;
  isProRata: boolean;
  takenDays: number;
  pendingDays: number;
  remainingDays: number;
  carriedOverDays: number;
  accrualRate: number | null;
  countryPackBadge: string;
  /**
   * Where the entitlement figure came from.
   *
   *   recorded             a figure somebody put on their record. Wins.
   *   company_allowance    company weeks x their working days a week.
   *   statutory_no_pattern no shift pattern, so the statutory figure shows.
   */
  entitlementBasis: 'recorded' | 'company_allowance' | 'statutory_no_pattern';
  /** Working days a week from the shift pattern. Null when none is recorded. */
  daysPerWeek: number | null;
  /** The company's terms are below this person's legal minimum. */
  belowStatutoryMinimum: boolean;
  /** Gross earned as at today - whole completed months of the holiday year. */
  accruedDays: number;
  /** Earned less taken and pending. Negative when they are ahead of themselves. */
  accruedRemainingDays: number;
  /** first_year | regular | irregular_hours - derived from the start date. */
  accrualBasis: string;
  /** Irregular hours only: hours earned this year, for when no day length is on record. */
  accruedHours: number;
  /** Twelfths credited so far. */
  accrualMonthsCredited: number;
  /** False past the first year - the allowance is already theirs. */
  accrualBlocksBooking: boolean;
}

export interface EntitlementsPayload {
  rows: EntitlementRow[];
  countryCode: string;
  countryPackBadge: string;
  leaveYear: number;
  /** "1 Apr 2026 to 31 Mar 2027" - the tenant's own year. */
  leaveYearLabel: string;
  leaveYearMode: string;
  /** How many employees have no accrual rule recorded. */
  unclassifiedCount: number;
}

export interface BradfordRow {
  employeeId: string;
  employeeInitials: string;
  employeeName: string;
  /** Added 2026-09-11 so the Bradford table can be filtered by department. */
  department: string | null;
  spells: number;
  totalDays: number;
  score: number;
  threshold: BradfordThreshold;
  lastAbsenceDate: string | null;
  lastAbsenceOngoing: boolean;
}

export interface BradfordPayload {
  rows: BradfordRow[];
  referencePeriod: '52_weeks';
  countryPackBadge: string;
}

export interface CreateLeaveRequestPayload {
  employeeId: string;
  leaveType: LeaveType;
  startDate: string; // ISO yyyy-mm-dd
  endDate: string;
  /** Derived by the server from the shift pattern; sent only to be checked. */
  workingDays?: number;
  employeeNote?: string | null;
  keyDate?: string | null;
  neonatalCareDays?: number | null;
  /** The booker has read the statutory-limit warning and keeps the booking. */
  acknowledgeLimit?: boolean;
}

export async function fetchHolidayCalendar(
  opts: { startDate?: string; endDate?: string; department?: string; leaveType?: LeaveType; search?: string } = {},
  signal?: AbortSignal,
): Promise<CalendarPayload> {
  return apiFetch<CalendarPayload>('/api/holiday/calendar', {
    method: 'GET',
    query: {
      startDate: opts.startDate || null,
      endDate: opts.endDate || null,
      department: opts.department || null,
      leaveType: opts.leaveType || null,
      search: opts.search || null,
    },
    signal,
  });
}

export async function fetchHolidayRequests(signal?: AbortSignal): Promise<ListLeaveRequestsResult> {
  return apiFetch<ListLeaveRequestsResult>('/api/holiday/requests', { method: 'GET', signal });
}

export async function fetchHolidayEntitlements(
  opts: { year?: number } = {},
  signal?: AbortSignal,
): Promise<EntitlementsPayload> {
  return apiFetch<EntitlementsPayload>('/api/holiday/entitlements', {
    method: 'GET',
    query: { year: opts.year ?? null },
    signal,
  });
}

export async function fetchHolidayBradford(signal?: AbortSignal): Promise<BradfordPayload> {
  return apiFetch<BradfordPayload>('/api/holiday/bradford', { method: 'GET', signal });
}

export async function fetchHolidayBadge(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/holiday/badge', { method: 'GET', signal });
  return data.count;
}

export async function createHolidayRequest(
  payload: CreateLeaveRequestPayload,
): Promise<{ request: PublicLeaveRequest }> {
  return apiFetch<{ request: PublicLeaveRequest }>('/api/holiday/requests', {
    method: 'POST',
    body: payload,
  });
}

export async function approveHolidayRequest(
  id: string,
  managerNote?: string | null,
): Promise<{ request: PublicLeaveRequest }> {
  return apiFetch<{ request: PublicLeaveRequest }>(`/api/holiday/requests/${id}/approve`, {
    method: 'PATCH',
    body: managerNote ? { managerNote } : {},
  });
}

export async function declineHolidayRequest(
  id: string,
  managerNote: string,
): Promise<{ request: PublicLeaveRequest }> {
  return apiFetch<{ request: PublicLeaveRequest }>(`/api/holiday/requests/${id}/decline`, {
    method: 'PATCH',
    body: { managerNote },
  });
}

/**
 * Cancel leave that is already BOOKED — item 14, 2026-10-03.
 *
 * Decline is for a request nobody has decided yet. This is for one that was
 * approved and is now not happening: the days go back on the entitlement, the
 * employee is told, and for sick leave the absence rows written at approval
 * are removed so the spell stops counting towards Bradford.
 *
 * The reason is required, not optional. It is what the employee reads, and
 * "your leave is cancelled" with nothing after it is how somebody turns up on
 * a day they thought they were off.
 */
export async function cancelHolidayRequest(
  id: string,
  reason: string,
  restoreAwops: boolean,
): Promise<{ request: PublicLeaveRequest }> {
  return apiFetch<{ request: PublicLeaveRequest }>(`/api/holiday/requests/${id}/cancel`, {
    method: 'PATCH',
    /* Sent explicitly either way, never omitted. The API defaults an absent
       value to false, so leaving it out would work — but "the manager was
       asked and said no" and "nobody asked" are different facts and the
       request body is where that distinction is visible. */
    body: { reason, restoreAwops },
  });
}

/**
 * The days this leave is covering that were marked absent-without-permission.
 *
 * Asked BEFORE the cancel, so the manager can be told what cancelling would
 * leave unexplained. Approving leave over an AWOP day converts it and takes it
 * out of the Bradford factor; cancelling leaves the day recorded as covered by
 * leave that no longer exists.
 */
export async function fetchLeaveCoveredAwops(
  id: string,
): Promise<{ dates: string[] }> {
  return apiFetch<{ dates: string[] }>(`/api/holiday/requests/${id}/covered-awops`, {
    method: 'GET',
  });
}

// ---------------------------------------------------------------------------
// Who else is off — the clash check (2026-09-11)
// ---------------------------------------------------------------------------
//
// Reagan: "when someone books a leave request it needs to look at the calnder
// and see if anyone is off that day aswell or if anyone else has asked for a
// holiday off", scoped by "i think it just needs the departments to see whos
// off at the same time not really every employee".
//
// The endpoint has existed and been tested since yesterday and nothing called
// it. These are the types and the fetcher that make it reachable from a screen.

/** One person in the same department whose leave overlaps the dates asked for. */
export interface LeaveClash {
  employeeId: string;
  employeeName: string;
  employeeInitials: string;
  leaveType: LeaveType;
  /**
   * 'approved' | 'taken' — they ARE off.
   * 'pending'            — they have asked and nobody has decided yet.
   *
   * Kept apart rather than collapsed into "unavailable": one is a fact to plan
   * around, the other is a decision still open, and approving both is how two
   * people end up off the same week by two managers on the same afternoon.
   */
  status: LeaveStatus;
  startDate: string;
  endDate: string;
  /** Days of the REQUESTED range they overlap — a one-day brush reads as one. */
  overlappingDays: number;
}

export interface LeaveClashPayload {
  /** The department the check was scoped to, for the wording on screen. */
  departmentName: string | null;
  /**
   * True when the employee has no department — NOT the same as "no clashes".
   * Nothing could be checked, and the screen has to say so rather than show a
   * reassuring empty list.
   */
  unscoped: boolean;
  clashes: LeaveClash[];
  approvedCount: number;
  pendingCount: number;
}

/**
 * READ-ONLY, and safe to call again every time the dates change: it warns, it
 * never refuses and it never writes.
 *
 * `excludeRequestId` is for re-checking a request that already exists —
 * without it an approver sees the very request they are deciding listed as a
 * clash with itself.
 */
export async function fetchLeaveClashes(
  opts: {
    employeeId: string;
    startDate: string;
    endDate: string;
    excludeRequestId?: string;
  },
  signal?: AbortSignal,
): Promise<LeaveClashPayload> {
  return apiFetch<LeaveClashPayload>('/api/holiday/requests/clashes', {
    method: 'GET',
    query: {
      employeeId: opts.employeeId,
      startDate: opts.startDate,
      endDate: opts.endDate,
      excludeRequestId: opts.excludeRequestId ?? null,
    },
    signal,
  });
}

export interface LeaveLimitResult {
  leaveType: 'carer' | 'neonatal' | 'parental_bereavement';
  weekDays: number;
  allowanceDays: number;
  alreadyBookedDays: number;
  remainingDays: number;
  overByDays: number;
  windowEnd: string | null;
  outsideWindow: boolean;
  warning: string | null;
}

export interface LeaveBookingPreview {
  /** The working days the server will store — the person's pattern and bank holidays. */
  workingDays: number;
  limit: LeaveLimitResult | null;
  limitMode: 'warn' | 'refuse';
}

/** What a booking would take and leave, from the server's own calculations. */
export async function fetchLeaveBookingPreview(
  opts: {
    employeeId: string;
    leaveType: LeaveType;
    startDate: string;
    endDate: string;
    keyDate?: string | null;
    neonatalCareDays?: number | null;
  },
  signal?: AbortSignal,
): Promise<LeaveBookingPreview> {
  return apiFetch<LeaveBookingPreview>('/api/holiday/requests/preview', {
    method: 'GET',
    query: {
      employeeId: opts.employeeId,
      leaveType: opts.leaveType,
      startDate: opts.startDate,
      endDate: opts.endDate,
      keyDate: opts.keyDate ?? null,
      neonatalCareDays: opts.neonatalCareDays ?? null,
    },
    signal,
  });
}

// ---------------------------------------------------------------------------
// Import / Export — Tab 4 (Section 7 / H-3)
// ---------------------------------------------------------------------------

import { getAccessToken } from './auth';

export type HolidayExportType = 'requests' | 'entitlements' | 'calendar';

/**
 * Download the leave-request import template.
 *
 * Added 2026-08-16. The tab reached this with a bare <a href="/api/holiday/
 * import-template">, which sends no Authorization header — the router is
 * behind authenticateToken and there is no cookie fallback, so it answered
 * 401 and the browser navigated the tab to the JSON error, losing the page.
 * IMPORT-EXPORT-AUDIT-001 FAULT 5.
 */
export async function downloadHolidayImportTemplate(): Promise<void> {
  await apiDownload('/api/holiday/import-template', 'holiday-import-template.csv');
}

export async function downloadHolidayExport(type: HolidayExportType): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(apiUrl(`/api/holiday/export?type=${encodeURIComponent(type)}`), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error('Export failed');
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m?.[1] ?? `holiday-${type}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ImportRequestsResult {
  created: number;
  skipped: number;
  errors: number;
  errorDetails: string[];
}

export async function importHolidayRequests(file: File): Promise<ImportRequestsResult> {
  const csv = await file.text();
  return apiFetch<ImportRequestsResult>('/api/holiday/import/requests', {
    method: 'POST',
    body: { csv },
  });
}

// ---------------------------------------------------------------------------
// Maternity records (MATB1) — Shane, 2026-08-14: "matb1 is to be in holiday and
// absence but needs to feed payroll. Same as SSP."
// ---------------------------------------------------------------------------

export type MaternityProofType =
  | 'matb1'
  | 'doctors_letter'
  | 'matching_certificate'
  | 'declaration';
/** The four statutory parental payments. They share every eligibility test and
 *  differ only in the key date, the evidence, and how many weeks are paid. */
export type ParentalLeaveType = 'maternity' | 'paternity' | 'adoption' | 'shared_parental';
export type MaternityOutcome = 'expected' | 'live_birth' | 'stillbirth' | 'died_after_birth';

export interface MaternityRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  leaveType: ParentalLeaveType;
  /** Expected week of childbirth, or the MATCHING date for an adoption. */
  keyDate: string;
  proofType: MaternityProofType | null;
  proofReceivedAt: string | null;
  noticeGivenAt: string | null;
  noticeExcuseAccepted: boolean;
  intendedLeaveStart: string | null;
  actualBirthDate: string | null;
  outcome: MaternityOutcome;
  smp1IssuedAt: string | null;
  smp1Reason: string | null;
  weeksNotPaidReason: string | null;
  notes: string | null;
}

/**
 * The eligibility answer, computed server-side by prepareSmpStatus.
 *
 * Every test's outcome, not a bare boolean — refusing SMP means issuing an SMP1
 * that names WHICH test failed, and "not eligible" cannot produce that.
 */
export interface SmpStatus {
  employeeId: string;
  leaveType: ParentalLeaveType;
  statutoryType: 'SMP' | 'SAP' | 'SPP' | 'ShPP';
  /** Weeks this payment runs for. Shared parental is a MAXIMUM — what is
   *  actually left depends on the other parent's claim. */
  maxWeeks: number;
  parentalLeaveRecordId: string;
  keyDate: string;
  qualifyingWeekStart: string;
  /** Null for adoption: the placement date, which the record does not hold (RA-68). */
  earliestLeaveStart: string | null;
  leaveStart: string | null;
  relevantPeriodAweWeekly: number;
  eligible: boolean;
  continuousEmploymentMet: boolean;
  earningsMet: boolean;
  proofMet: boolean;
  noticeMet: boolean;
  proofDeadlinePassed: boolean;
  veryPremature: boolean;
  reasons: string[];
}

export interface MaternityView {
  records: MaternityRecord[];
  status: SmpStatus[];
}

export async function fetchMaternity(signal?: AbortSignal): Promise<MaternityView> {
  return apiFetch<MaternityView>('/api/holiday/maternity', { method: 'GET', signal });
}

export interface SaveMaternityInput {
  employeeId: string;
  leaveType: ParentalLeaveType;
  keyDate: string;
  proofType?: MaternityProofType | null;
  proofReceivedAt?: string | null;
  noticeGivenAt?: string | null;
  noticeExcuseAccepted?: boolean;
  intendedLeaveStart?: string | null;
  actualBirthDate?: string | null;
  outcome?: MaternityOutcome;
  smp1IssuedAt?: string | null;
  smp1Reason?: string | null;
  weeksNotPaidReason?: string | null;
  notes?: string | null;
}

export async function saveMaternityRecord(input: SaveMaternityInput): Promise<MaternityRecord> {
  return apiFetch<MaternityRecord>('/api/holiday/maternity', { method: 'POST', body: input });
}

// ---------------------------------------------------------------------------
// Return to Work — submit only (2026-08-24)
// ---------------------------------------------------------------------------
//
// The form moved onto the Bradford Factor page, so it posts to THIS section.
// Reagan: "this is a form to fill in for people to come back to work. when this
// is signed and done this again will be saved to the employee account in the hr
// and people so only hr and people can actually see that."
//
// There is deliberately no read here. The history stays on hrApi's
// fetchReturnToWork, behind `hr_people` — a Holiday user submits one and cannot
// look back through them.
//
// It posts to /api/holiday rather than /api/hr because `verifyTenant` resolves
// the section key from the URL: calling /api/hr from a Holiday-only user would
// 403, and a list with no error branch renders that as "nothing here". That is
// the Appraisals fault of 22 Aug, written up in SPEC-46 section 3.
export async function submitReturnToWork(
  employeeId: string,
  payload: ReturnToWorkPayload,
): Promise<PublicReturnToWorkForm> {
  const data = await apiFetch<{ form: PublicReturnToWorkForm }>(
    `/api/holiday/return-to-work/${employeeId}`,
    { method: 'POST', body: payload },
  );
  return data.form;
}
